import classNames from 'classnames';
import { FC, FocusEventHandler, ReactNode, useCallback, useRef, useState } from 'react';
import { Key } from '@react-types/shared';
import { Autocomplete as HeroUIAutocomplete, AutocompleteItem } from '@heroui/autocomplete';

import { PropsWithClassname } from '@/app/types';

export interface AutocompleteOption {
  id: Key;
  name: string;
}

export interface AutocompleteProps extends PropsWithClassname {
  id: string;
  value: string;
  options?: AutocompleteOption[];
  onSelectOption: (id: Key | null) => void;
  onChange: (value: string) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  label: string;
  isErrorVisible?: boolean;
  errorMessage?: ReactNode;
  note?: string;
  after?: string;
}

const Autocomplete: FC<AutocompleteProps> = ({
  id,
  options,
  onSelectOption,
  onChange,
  onFocus,
  onBlur,
  label,
  isErrorVisible,
  errorMessage,
  note,
  value,
  after,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedKey, setSelectedKey] = useState<Key | null | undefined>(null);

  const handleChange = useCallback(
    (keyword: string) => {
      if (!keyword) {
        setSelectedKey(null);
      }

      onChange(keyword);
    },
    [onChange],
  );

  const handleSelectOption = useCallback(
    (key: Key | null) => {
      setSelectedKey(key);
      onSelectOption(key);
    },
    [onSelectOption],
  );

  return (
    <div className={classNames(className, '')}>
      {!!label && (
        <label id={`${id}-label`} className="block mb-[8px] font-medium text-[16px] leading-[24px]">
          {label}
        </label>
      )}
      {!!errorMessage && isErrorVisible && <div className="text-warning font-medium mb-[8px]">{errorMessage}</div>}
      <div ref={containerRef} className="relative">
        <HeroUIAutocomplete
          inputValue={value}
          items={options}
          aria-labelledby={`#${id}-label`}
          selectedKey={selectedKey}
          onInputChange={handleChange}
          onSelectionChange={handleSelectOption}
          onFocus={onFocus}
          onBlur={onBlur}
          selectorIcon={null}
          allowsCustomValue
          allowsEmptyCollection
          classNames={{
            base: classNames(
              className,
              'w-full border border-neutral-midLight focus:border-neutral-dark rounded text-neutral-dark outline-0',
              {
                'border-warning focus:border-warning has-error': isErrorVisible,
                'mb-[8px]': !!note,
                'mb-[32px]': !note,
              },
            ),
            listboxWrapper: 'bg-white py-[12px] rounded text-neutral-dark w-full border border-neutral-light',
          }}
          inputProps={{
            classNames: {
              inputWrapper: 'shadow-none px-[24px] py-[12px]',
            },
          }}
          listboxProps={{
            emptyContent: null,
          }}
        >
          {(item) => (
            <AutocompleteItem
              key={item.id}
              className="bg-white hover:bg-primary-light transition-colors px-[24px] py-[4px]"
            >
              {item.name}
            </AutocompleteItem>
          )}
        </HeroUIAutocomplete>
        {!!after && <span className="absolute right-[16px] text-neutral-semiTransparent top-[12px]">{after}</span>}
      </div>
      {!!note && <small className="block text-[12px] leading-[1.4] text-neutral-mid mb-[32px]">{note}</small>}
    </div>
  );
};

export default Autocomplete;
